import { useState } from 'react';

import { Button } from '@/components/atoms';
import { useCreateStory, useStories } from '@/hooks/useStories';
import type { StorySummary } from '@/types/story';

import styles from './StoryPicker.module.css';

export interface StoryPickerProps {
  open: boolean;
  onClose: () => void;
  onPick: (story: StorySummary) => void;
}

type Mode = 'pick' | 'create';

export function StoryPicker({ open, onClose, onPick }: StoryPickerProps) {
  const [mode, setMode] = useState<Mode>('pick');
  const [search, setSearch] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const { data: stories, isLoading } = useStories({
    status: 'active',
    search: search || undefined,
    limit: 50,
  });
  const createMutation = useCreateStory();

  if (!open) return null;

  async function handleCreate() {
    if (!newTitle.trim()) return;
    try {
      const story = await createMutation.mutateAsync({
        title: newTitle.trim(),
        description: newDesc.trim() || undefined,
      });
      onPick(story);
    } catch {
      // hook handles state
    }
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2 className={styles.title}>Escolher story</h2>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Fechar"
          >
            ×
          </button>
        </header>

        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${mode === 'pick' ? styles.tabActive : ''}`}
            onClick={() => setMode('pick')}
          >
            Story existente
          </button>
          <button
            className={`${styles.tab} ${mode === 'create' ? styles.tabActive : ''}`}
            onClick={() => setMode('create')}
          >
            Criar nova
          </button>
        </div>

        <div className={styles.body}>
          {mode === 'pick' && (
            <>
              <input
                type="search"
                placeholder="Buscar título…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={styles.input}
              />
              {isLoading && <p className={styles.muted}>A carregar…</p>}
              {stories && stories.length === 0 && (
                <p className={styles.muted}>
                  Nenhuma story activa. Cria uma nova ao lado.
                </p>
              )}
              <ul className={styles.list}>
                {stories?.map((s) => (
                  <li key={s.id}>
                    <button
                      className={styles.storyRow}
                      onClick={() => onPick(s)}
                    >
                      <strong>{s.title}</strong>
                      <span className={styles.meta}>
                        {s.manifestations_count} manifestações
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {mode === 'create' && (
            <>
              <input
                type="text"
                placeholder="Título da story…"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                className={styles.input}
                autoFocus
              />
              <textarea
                placeholder="Descrição (opcional)"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                rows={2}
                className={styles.textarea}
              />
              <Button
                onClick={handleCreate}
                disabled={!newTitle.trim()}
                loading={createMutation.isPending}
              >
                Criar e usar
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
